import { useMemo, useState } from 'react'
import { Space } from 'antd'
import { Input } from './Input'
import { Select } from './Select'
import {
  COUNTRY_DIAL_CODES,
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
  joinPhoneNumber,
  splitPhoneNumber,
} from '@/lib/countryDialCodes'

export interface PhoneNumberInputProps {
  /** The stored number, e.g. '+60123456789'. */
  value?: string | null
  onChange: (value: string) => void
  /** ISO alpha-2 codes to list first, from the field's configuration. */
  pinnedCountryCodes?: string[]
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

/**
 * A country code picker beside a number input, storing both as the single
 * string the rest of the platform treats as a phone number ('+60123456789').
 */
export function PhoneNumberInput({
  value,
  onChange,
  pinnedCountryCodes = [],
  placeholder = 'Enter phone number',
  disabled = false,
  size = 'small',
}: PhoneNumberInputProps) {
  // An empty field has no country to read back from its value, so the last
  // choice is held here. Picking a country then typing the number is the usual
  // order, and the choice has to survive that gap.
  const [chosenIso, setChosenIso] = useState<string | null>(null)

  const pinned = useMemo(
    () => pinnedCountryCodes.map((iso) => iso?.toUpperCase()).filter(Boolean),
    [pinnedCountryCodes]
  )

  const options = useMemo(() => {
    const pinnedCountries = pinned.map(findCountryByIso).filter(Boolean) as typeof COUNTRY_DIAL_CODES
    const rest = COUNTRY_DIAL_CODES.filter((country) => !pinned.includes(country.iso))
    return [...pinnedCountries, ...rest].map((country) => ({
      value: country.iso,
      // Searchable by country name as well as by the code itself.
      label: `+${country.dialCode} ${country.name}`,
    }))
  }, [pinned])

  // Countries sharing a dial code are resolved in favour of the current choice,
  // then the pinned ones, so '+1' does not drift off the chosen country.
  const preferredIsos = useMemo(() => [chosenIso, ...pinned], [chosenIso, pinned])
  const { iso: parsedIso, nationalNumber } = useMemo(
    () => splitPhoneNumber(value, preferredIsos),
    [value, preferredIsos]
  )
  const selectedIso = parsedIso || chosenIso || pinned[0] || DEFAULT_COUNTRY_ISO

  const handleCountryChange = (iso: string) => {
    setChosenIso(iso)
    onChange(joinPhoneNumber(iso, nationalNumber))
  }

  const handleNumberChange = (typed: string) => {
    // A pasted international number carries its own country code, so it
    // replaces the picker rather than being appended to it.
    const trimmed = typed.trim()
    if (trimmed.startsWith('+')) {
      const pasted = splitPhoneNumber(trimmed, preferredIsos)
      if (pasted.iso) {
        setChosenIso(pasted.iso)
        onChange(joinPhoneNumber(pasted.iso, pasted.nationalNumber))
        return
      }
    }

    onChange(joinPhoneNumber(selectedIso, trimmed))
  }

  return (
    <Space.Compact className="w-full">
      <Select
        value={selectedIso}
        onChange={handleCountryChange}
        options={options}
        disabled={disabled}
        size={size}
        showSearch
        // The dropdown lists "+60 Malaysia" but the closed control only needs
        // the code, so a long country name cannot squeeze out the number.
        labelRender={(option) => `+${findCountryByIso(String(option.value))?.dialCode ?? ''}`}
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
        style={{ width: '38%' }}
      />
      <Input
        value={nationalNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        inputMode="tel"
        style={{ width: '62%' }}
      />
    </Space.Compact>
  )
}
